/**
 * Usage accounting rules for the private workflow (M15). Pure functions, no I/O.
 *
 *  - A session's total is its own (unparented) `usage` cumulative: the CLI already folds its
 *    subagents into that number, so child usage (`usage` events with a `parentId`, and
 *    `cli.task` usage) is reported as a *subset* and is never added on top.
 *  - Only when a session reports no total of its own do children stand in for it, and then the
 *    result is marked partial rather than presented as a complete figure.
 *  - Money is one of four kinds, never blended: `reported` (the provider CLI stated a cost),
 *    `estimate` (computed here from tokens with caller-supplied rates), `missing` (nothing to
 *    go on: Codex reports tokens but no cost), and subscription headroom, which is a separate
 *    quota-window reading, not a charge.
 *  - Every reading carries its observation time so freshness can be shown; nothing is inferred.
 */
import type { RunEfficiency, RunUsageSummary, TokenCounts, TranscriptEvent } from '@viko/protocol'

/** Above vikod's 20-minute unchanged-status heartbeat (burst-guard), so idle usage stays fresh. */
export const USAGE_STALE_MS = 30 * 60_000

export interface TokenRates {
  /** USD per million tokens. */
  inputPerM: number
  outputPerM: number
  cacheReadPerM?: number
  cacheWritePerM?: number
}

export type Charge =
  | { kind: 'reported'; usd: number; observedAt: number }
  | { kind: 'estimate'; usd: number; observedAt: number }
  | { kind: 'missing' }

export interface Headroom {
  /** Worst (most used) window. */
  window: string
  usedPct: number
  remainingPct: number
  resetsAt?: number
  status?: 'allowed' | 'warning' | 'rejected'
  observedAt: number
}

export interface SessionUsage {
  tokens?: TokenCounts
  /** True when `tokens` are only what subagents reported (no session total was reported). */
  partial: boolean
  charge: Charge
  children: {
    count: number
    tokens: number
    /** Always true when a session total exists: shown as a breakdown, never added. */
    includedInTotal: boolean
  }
  headroom?: Headroom
  contextPct?: number
  observedAt?: number
}

const ZERO: TokenCounts = { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 }

export function totalTokens(t: TokenCounts): number {
  return t.total ?? t.input + t.output + t.cacheRead + t.cacheWrite
}

function add(a: TokenCounts, b: TokenCounts): TokenCounts {
  const out: TokenCounts = {
    input: a.input + b.input,
    output: a.output + b.output,
    cacheRead: a.cacheRead + b.cacheRead,
    cacheWrite: a.cacheWrite + b.cacheWrite,
  }
  if (a.reasoning !== undefined || b.reasoning !== undefined)
    out.reasoning = (a.reasoning ?? 0) + (b.reasoning ?? 0)
  if (a.total !== undefined || b.total !== undefined) out.total = totalTokens(a) + totalTokens(b)
  return out
}

/** Cost from tokens with explicit rates. No built-in price table: unknown rates stay unknown. */
export function estimateUsd(t: TokenCounts, rates: TokenRates): number {
  return (
    (t.input * rates.inputPerM +
      t.output * rates.outputPerM +
      t.cacheRead * (rates.cacheReadPerM ?? rates.inputPerM) +
      t.cacheWrite * (rates.cacheWritePerM ?? rates.inputPerM)) /
    1_000_000
  )
}

/** Worst-window headroom from the newest `ratelimit` reading. */
export function headroomOf(
  windows: readonly {
    window: string
    usedPct?: number
    resetsAt?: number
    status?: 'allowed' | 'warning' | 'rejected'
  }[],
  observedAt: number,
): Headroom | undefined {
  let worst: Headroom | undefined
  for (const w of windows) {
    if (w.usedPct === undefined) continue
    const used = Math.min(100, Math.max(0, w.usedPct))
    if (!worst || used > worst.usedPct)
      worst = {
        window: w.window,
        usedPct: used,
        remainingPct: 100 - used,
        ...(w.resetsAt !== undefined ? { resetsAt: w.resetsAt } : {}),
        ...(w.status ? { status: w.status } : {}),
        observedAt,
      }
  }
  return worst
}

export function isStale(observedAt: number | undefined, now: number): boolean {
  return observedAt === undefined || now - observedAt > USAGE_STALE_MS
}

/** Fold a run's events (any order within seq) into one non-double-counted account. */
export function accountSessionUsage(
  events: readonly TranscriptEvent[],
  opts: { rates?: TokenRates } = {},
): SessionUsage {
  let own: { e: TranscriptEvent & { kind: 'usage' }; seq: number } | undefined
  let ownTurns: TokenCounts | undefined
  let lastCost: { usd: number; ts: number } | undefined
  let contextPct: number | undefined
  let observedAt: number | undefined
  let rate: (TranscriptEvent & { kind: 'ratelimit' }) | undefined
  const childUsage = new Map<string, { tokens: TokenCounts; seq: number }>()
  const childTasks = new Map<string, number>()

  for (const e of events) {
    if (e.kind === 'usage') {
      if (e.parentId) {
        // Child scope: latest cumulative per parent, else the sum of its turn deltas.
        const cur = childUsage.get(e.parentId)
        const next = e.cumulative ?? (e.turn ? add(cur?.tokens ?? ZERO, e.turn) : undefined)
        if (next && (!cur || e.cumulative || e.seq >= cur.seq))
          childUsage.set(e.parentId, { tokens: next, seq: e.seq })
        continue
      }
      if (!own || e.seq >= own.seq) own = { e, seq: e.seq }
      if (e.cumulative === undefined && e.turn) ownTurns = add(ownTurns ?? ZERO, e.turn)
      if (e.costUsd !== undefined && (!lastCost || e.ts >= lastCost.ts))
        lastCost = { usd: e.costUsd, ts: e.ts }
      if (e.contextPct !== undefined) contextPct = e.contextPct
      observedAt = Math.max(observedAt ?? 0, e.ts)
    } else if (e.kind === 'cli.task' && e.usage?.totalTokens !== undefined) {
      childTasks.set(e.taskId, Math.max(childTasks.get(e.taskId) ?? 0, e.usage.totalTokens))
    } else if (e.kind === 'ratelimit') {
      if (!rate || e.seq >= rate.seq) rate = e
    }
  }

  const totals = own?.e.cumulative ?? ownTurns
  const childSum = [...childUsage.values()].reduce((n, c) => n + totalTokens(c.tokens), 0)
  const taskSum = [...childTasks.values()].reduce((n, t) => n + t, 0)
  // Two views of the same subagents: the larger is the more complete, they are never summed.
  const childTokens = Math.max(childSum, taskSum)
  const childCount = Math.max(childUsage.size, childTasks.size)

  let tokens = totals
  let partial = false
  if (!tokens && childUsage.size) {
    tokens = [...childUsage.values()].reduce<TokenCounts>((a, c) => add(a, c.tokens), ZERO)
    partial = true
  }

  let charge: Charge = { kind: 'missing' }
  if (lastCost) charge = { kind: 'reported', usd: lastCost.usd, observedAt: lastCost.ts }
  else if (tokens && opts.rates && !partial)
    charge = {
      kind: 'estimate',
      usd: estimateUsd(tokens, opts.rates),
      observedAt: observedAt ?? 0,
    }

  const headroom = rate ? headroomOf(rate.windows, rate.ts) : undefined
  return {
    ...(tokens ? { tokens } : {}),
    partial,
    charge,
    children: { count: childCount, tokens: childTokens, includedInTotal: !partial },
    ...(headroom ? { headroom } : {}),
    ...(contextPct !== undefined ? { contextPct } : {}),
    ...(observedAt !== undefined ? { observedAt } : {}),
  }
}

export function formatUsd(usd: number): string {
  return usd < 0.01 && usd > 0 ? '<$0.01' : `$${usd.toFixed(2)}`
}

export function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`
  return String(n)
}

/** Plain-language label for a charge, always naming what kind of figure it is. */
export function chargeLabel(c: Charge): string {
  switch (c.kind) {
    case 'reported':
      return `${formatUsd(c.usd)} reported`
    case 'estimate':
      return `~${formatUsd(c.usd)} estimated`
    case 'missing':
      return 'Cost not reported'
  }
}

export function headroomLabel(h: Headroom | undefined, now: number): string {
  if (!h) return 'Headroom unknown'
  const stale = isStale(h.observedAt, now)
  return `${Math.round(h.remainingPct)}% ${h.window} headroom${stale ? ' (stale)' : ''}`
}

/** The same rules applied to the live transcript view (the reducer already keeps children apart). */
export function accountViewUsage(
  view: {
    usage: {
      cumulative?: TokenCounts
      costUsd?: number
      contextPct?: number
      observedAt?: number
      children?: { byParent: Record<string, number>; byTask: Record<string, number> }
    } | null
    ratelimits: readonly {
      window: string
      usedPct?: number
      resetsAt?: number
      status?: 'allowed' | 'warning' | 'rejected'
    }[]
    ratelimitAt?: number
  },
  opts: { rates?: TokenRates } = {},
): SessionUsage {
  const u = view.usage
  const sum = (r: Record<string, number> | undefined) =>
    Object.values(r ?? {}).reduce((n, t) => n + t, 0)
  const byParent = u?.children?.byParent,
    byTask = u?.children?.byTask
  const tokens = u?.cumulative
  const observedAt = u?.observedAt ?? 0
  let charge: Charge = { kind: 'missing' }
  if (u?.costUsd !== undefined) charge = { kind: 'reported', usd: u.costUsd, observedAt }
  else if (tokens && opts.rates)
    charge = { kind: 'estimate', usd: estimateUsd(tokens, opts.rates), observedAt }
  const headroom =
    view.ratelimits.length && view.ratelimitAt !== undefined
      ? headroomOf(view.ratelimits, view.ratelimitAt)
      : undefined
  return {
    ...(tokens ? { tokens } : {}),
    partial: false,
    charge,
    children: {
      count: Math.max(Object.keys(byParent ?? {}).length, Object.keys(byTask ?? {}).length),
      tokens: Math.max(sum(byParent), sum(byTask)),
      includedInTotal: true,
    },
    ...(headroom ? { headroom } : {}),
    ...(u?.contextPct !== undefined ? { contextPct: u.contextPct } : {}),
    ...(u?.observedAt !== undefined ? { observedAt: u.observedAt } : {}),
  }
}

type ViewUsageInput = Parameters<typeof accountViewUsage>[0]

/** Latest encrypted usage report for a run (row projection): the fallback when no transcript is open. */
export interface ReportedRunUsage {
  costUsd?: number
  /** Table-based estimate from the daemon's run summary (used only when no cost was reported). */
  estimatedUsd?: number
  observedAt?: number
  tokens?: TokenCounts
  models?: readonly string[]
  /** Subagent usage from the run summary: a subset of `tokens`, shown separately. */
  children?: { count: number; tokens: number }
  /** G18 efficiency findings and tokens by model tier from the run summary. */
  efficiency?: RunEfficiency
}

/**
 * THE usage selector: every surface (Now/Recent rows, session header chips, progress stats,
 * project board, inbox) reads a run's usage through this. The decrypted transcript wins when
 * it has any usage reading; else the latest encrypted usage report; "missing" only when
 * neither has data. Freshness is `observedAt` of whichever source was chosen.
 */
export function selectRunUsage(
  src: { view?: ViewUsageInput | null; report?: ReportedRunUsage | undefined },
  opts: { rates?: TokenRates } = {},
): SessionUsage {
  const v = src.view
  const fromView = v?.usage && (v.usage.costUsd !== undefined || v.usage.cumulative)
  if (v && fromView) return accountViewUsage(v, opts)
  const r = src.report
  const base = v ? accountViewUsage(v, opts) : undefined
  if (r && (r.costUsd !== undefined || r.estimatedUsd !== undefined || r.tokens)) {
    const observedAt = r.observedAt ?? 0
    let charge: Charge = { kind: 'missing' }
    if (r.costUsd !== undefined) charge = { kind: 'reported', usd: r.costUsd, observedAt }
    else if (r.estimatedUsd !== undefined)
      charge = { kind: 'estimate', usd: r.estimatedUsd, observedAt }
    else if (r.tokens && opts.rates)
      charge = { kind: 'estimate', usd: estimateUsd(r.tokens, opts.rates), observedAt }
    return {
      ...(base ?? {
        partial: false,
        children: { count: 0, tokens: 0, includedInTotal: true },
      }),
      ...(r.children
        ? {
            children: { count: r.children.count, tokens: r.children.tokens, includedInTotal: true },
          }
        : {}),
      ...(r.tokens ? { tokens: r.tokens } : {}),
      charge,
      ...(r.observedAt !== undefined ? { observedAt: r.observedAt } : {}),
    }
  }
  return (
    base ?? {
      partial: false,
      charge: { kind: 'missing' },
      children: { count: 0, tokens: 0, includedInTotal: true },
    }
  )
}

/** Run-row projection of the same selector (row fields are the report fallback). */
export function selectRowUsage(
  run: {
    costUsd?: number
    usageObservedAt?: number
    updatedAt?: number
    /** The daemon's encrypted per-run summary (attached to list rows). */
    usageReport?: ReportedRunUsage
  },
  view?: ViewUsageInput | null,
  opts: { rates?: TokenRates } = {},
): SessionUsage {
  const fromRow: ReportedRunUsage | undefined =
    run.costUsd !== undefined
      ? { costUsd: run.costUsd, observedAt: run.usageObservedAt ?? run.updatedAt ?? 0 }
      : undefined
  const s = run.usageReport
  // Row cost (already the freshest decrypted reading) wins over the summary's own figures.
  const report = fromRow ? { ...s, ...fromRow } : s
  return selectRunUsage({ view, report }, opts)
}

/** A run summary from the daemon's encrypted `run.usage` report, as the selector's fallback. */
export function reportedUsageOf(s: RunUsageSummary): ReportedRunUsage {
  return {
    ...(s.reportedUsd !== undefined ? { costUsd: s.reportedUsd } : {}),
    ...(s.estimatedUsd !== undefined ? { estimatedUsd: s.estimatedUsd } : {}),
    ...(s.tokens ? { tokens: s.tokens } : {}),
    ...(s.models.length ? { models: s.models.map((m) => m.model) } : {}),
    ...(s.subagents ? { children: s.subagents } : {}),
    ...(s.efficiency ? { efficiency: s.efficiency } : {}),
    observedAt: s.updatedAt,
  }
}

/** Row with the decrypted transcript's cost folded in, so list surfaces match the header. */
export function withViewUsage<T extends object>(
  row: T,
  view: ViewUsageInput | null | undefined,
): T & { costUsd?: number; usageObservedAt?: number } {
  const u = view?.usage
  if (!u || u.costUsd === undefined) return row
  return {
    ...row,
    costUsd: u.costUsd,
    ...(u.observedAt !== undefined ? { usageObservedAt: u.observedAt } : {}),
  }
}

export interface AccountRef {
  id?: string
  label: string
  /** Set when the label is a guess from the agent's only known account rather than the run's own. */
  inferred: boolean
}

/**
 * Which provider account a run is using. The run's own signed `accountId` wins; otherwise the
 * machine's single enabled account for the agent; otherwise unknown (never a guess between two).
 */
export function selectedAccount(
  run: { accountId?: string; agent?: string; machineId?: string } | undefined,
  machines: readonly {
    id: string
    providerAccounts?: readonly { id: string; provider: string; label: string; enabled: boolean }[]
  }[] = [],
): AccountRef | undefined {
  if (!run) return undefined
  const accounts = machines.find((m) => m.id === run.machineId)?.providerAccounts ?? []
  if (run.accountId) {
    const known = accounts.find((a) => a.id === run.accountId)
    return { id: run.accountId, label: known?.label ?? run.accountId, inferred: false }
  }
  const same = accounts.filter((a) => a.provider === run.agent && a.enabled)
  if (same.length === 1 && same[0]) return { id: same[0].id, label: same[0].label, inferred: true }
  return undefined
}

/** Hub quota windows that belong to one provider account (`claude`, `codex`, `codex:work`). */
export function accountWindows<W extends { accountId?: string; agent?: string; usedPct: number }>(
  windows: readonly W[],
  account: string,
): (W & { remainingPct: number })[] {
  const agent = account.split(':')[0]
  return windows
    .filter((w) => (w.accountId ? w.accountId === account : w.agent === agent && account === agent))
    .map((w) => ({ ...w, remainingPct: Math.max(0, Math.min(100, 100 - w.usedPct)) }))
}
